<script lang="ts">
	import SalesIncome from "./SalesIncome.svelte";
	// utils
	import {
		formatISO,
		startOfDay,
		endOfDay,
		addDays,
		addWeeks,
		startOfWeek,
		endOfWeek,
		addMonths,
		startOfMonth,
		endOfMonth,
	} from "date-fns";
	import { t, api } from "../../../stores";

	export let today = new Date();
	export let storeIds: string[] = [];

	//functions
	function getTodayRevenue(date: Date, storeId: string) {
		const todaySalesPromise = $api.getStoreSalesReport({
			startTimeInTimeZone: formatISO(startOfDay(date)),
			endTimeInTimeZone: formatISO(endOfDay(date)),
			reportTimeZone: "ASIA_SEOUL",
			reportResolution: "DAILY",
			storeid: storeId,
		});
		const yesterdaySalesPromise = $api.getStoreSalesReport({
			startTimeInTimeZone: formatISO(startOfDay(addDays(date, -1))),
			endTimeInTimeZone: formatISO(endOfDay(addDays(date, -1))),
			reportTimeZone: "ASIA_SEOUL",
			reportResolution: "DAILY",
			storeid: storeId,
		});
		return Promise.all([todaySalesPromise, yesterdaySalesPromise]);
	}

	function getThisWeekRevenue(date: Date, storeId: string) {
		const thisWeekPromise = $api.getStoreSalesReport({
			startTimeInTimeZone: formatISO(startOfWeek(date)),
			endTimeInTimeZone: formatISO(endOfWeek(date)),
			reportTimeZone: "ASIA_SEOUL",
			reportResolution: "DAILY",
			storeid: storeId,
		});
		const lastWeekSalesPromise = $api.getStoreSalesReport({
			startTimeInTimeZone: formatISO(startOfWeek(addWeeks(date, -1))),
			endTimeInTimeZone: formatISO(endOfWeek(addWeeks(date, -1))),
			reportTimeZone: "ASIA_SEOUL",
			reportResolution: "DAILY",
			storeid: storeId,
		});
		return Promise.all([thisWeekPromise, lastWeekSalesPromise]);
	}

	function getThisMonthRevenue(date: Date, storeId: string) {
		const thisMonthPromise = $api.getStoreSalesReport({
			startTimeInTimeZone: formatISO(startOfMonth(date)),
			endTimeInTimeZone: formatISO(endOfMonth(date)),
			reportTimeZone: "ASIA_SEOUL",
			reportResolution: "MONTHLY",
			storeid: storeId,
		});
		const lastMonthSalesPromise = $api.getStoreSalesReport({
			startTimeInTimeZone: formatISO(startOfMonth(addMonths(date, -1))),
			endTimeInTimeZone: formatISO(endOfMonth(addMonths(date, -1))),
			reportTimeZone: "ASIA_SEOUL",
			reportResolution: "MONTHLY",
			storeid: storeId,
		});
		return Promise.all([thisMonthPromise, lastMonthSalesPromise]);
	}

	$: (async (date: Date, storeIds: string[]) => {
		const todayRevenue = await Promise.all(
			storeIds.map((v) => getTodayRevenue(date, v))
		);
		const thisWeekRevenue = await Promise.all(
			storeIds.map((v) => getThisWeekRevenue(date, v))
		);
		const thisMonthRevenue = await Promise.all(
			storeIds.map((v) => getThisMonthRevenue(date, v))
		);

		let todayQuantity = 0;
		let todayAmount = 0;
		let yesterdayAmount = 0;
		let weekQuantity = 0;
		let weekAmount = 0;
		let lastWeekAmount = 0;
		let monthQuantity = 0;
		let monthAmount = 0;
		let lastmonthAmount = 0;

		for (const promises of todayRevenue) {
			const [today, yesterday] = promises;
			todayQuantity += today.data.salesReports.reduce((prev, cur) => {
				return prev + cur.count;
			}, 0);
			todayAmount += today.data.salesReports.reduce((prev, cur) => {
				return prev + cur.sum;
			}, 0);
			yesterdayAmount += yesterday.data.salesReports.reduce((prev, cur) => {
				return prev + cur.sum;
			}, 0);
		}
		for (const promises of thisWeekRevenue) {
			const [thisWeek, lastWeek] = promises;
			weekQuantity += thisWeek.data.salesReports.reduce((prev, cur) => {
				return prev + cur.count;
			}, 0);
			weekAmount += thisWeek.data.salesReports.reduce((prev, cur) => {
				return prev + cur.sum;
			}, 0);
			lastWeekAmount += lastWeek.data.salesReports.reduce((prev, cur) => {
				return prev + cur.sum;
			}, 0);
		}
		for (const promises of thisMonthRevenue) {
			const [thisMonth, lastMonth] = promises;
			monthQuantity += thisMonth.data.salesReports.reduce((prev, cur) => {
				return prev + cur.count;
			}, 0);
			monthAmount += thisMonth.data.salesReports.reduce((prev, cur) => {
				return prev + cur.sum;
			}, 0);
			lastmonthAmount += lastMonth.data.salesReports.reduce((prev, cur) => {
				return prev + cur.sum;
			}, 0);
		}

		todaySalesReport.amount = todayAmount;
		todaySalesReport.quantity = todayQuantity;
		todaySalesReport.compareTargetAmount = yesterdayAmount;
		weekSalesReport.amount = weekAmount;
		weekSalesReport.quantity = weekQuantity;
		weekSalesReport.compareTargetAmount = lastWeekAmount;
		monthSalesReport.amount = monthAmount;
		monthSalesReport.quantity = monthQuantity;
		monthSalesReport.compareTargetAmount = lastmonthAmount;
	})(today, storeIds);

	let todaySalesReport = {
		quantity: 0,
		amount: 0,
		compareTargetAmount: 0,
	};
	let weekSalesReport = {
		quantity: 0,
		amount: 0,
		compareTargetAmount: 0,
	};
	let monthSalesReport = {
		quantity: 0,
		amount: 0,
		compareTargetAmount: 0,
	};

</script>

<div class="container">
	<div class="income">
		<SalesIncome
			quantity={todaySalesReport.quantity}
			amount={todaySalesReport.amount}
			compareTargetAmount={todaySalesReport.compareTargetAmount}
			title={$t('hub.dashboard.widget.today')}
			comparePeriod={$t('hub.dashboard.widget.compared_with_yesterday')} />
	</div>
	<div class="income">
		<SalesIncome
			quantity={weekSalesReport.quantity}
			amount={weekSalesReport.amount}
			compareTargetAmount={weekSalesReport.compareTargetAmount}
			title={$t('hub.dashboard.widget.week')}
			comparePeriod={$t('hub.dashboard.widget.compared_with_lastweek')} />
	</div>
	<div class="income">
		<SalesIncome
			quantity={monthSalesReport.quantity}
			amount={monthSalesReport.amount}
			compareTargetAmount={monthSalesReport.compareTargetAmount}
			title={$t('hub.dashboard.widget.month')}
			comparePeriod={$t('hub.dashboard.widget.compared_with_lastmonth')} />
	</div>
</div>

<style lang="scss">
	@import "../../../variables.scss";

	.container {
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: 10px;
	}

	@include DesktopSize {
		.container {
			flex-direction: row;
			gap: 20px;

			.income {
				flex-basis: calc((100% - 40px) / 3);
			}
		}
	}

</style>
